import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../redux/store";
import { UserAddress } from "../../../../shared/models/user/user-address";
import {
  Message,
  MessageSections,
} from "../../../../components/message/message";
import {
  Alert,
  DialogContent,
  Grid,
  ListItemText,
  MenuItem,
  PaperProps,
} from "@mui/material";
import userAddressesService, {
  UserAddressUpdateMeta,
} from "../../../../../services/user-addresses.service";
import { AxiosError } from "axios";
import BarekamInput from "../../../../components/input";
import { BarekamSelect } from "../../../../components/select/select";
import { CityModel } from "../../../../shared/models/cityModel";
import { ProvinceModel } from "../../../../shared/models/provinceModel";
import { mapValidationErrors } from "../../personal-info/personal-info.service";
import { StandardDialogFooter } from "../../../../components/dialogs/standard-dialog/standard-dialog";
import { CountryModel } from "../../../../shared/models/countryModel";
import { getMessage } from "../../../../shared/helper-functions";
import BarekamPhoneInput from "../../../../auth/phone-input/phone-input";

export const EditAddress = ({
  address,
  onSave,
  isRecipientAddress,
}: EditRecipientProps) => {
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const { countries, provinces, cities, regions } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);
  const { myAddressesData } = useAppSelector(
    (state) => state.userAddressesReducer,
  );

  const [newAddress, setNewAddress] = useState<UserAddress>(getAddress());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  function getAddress() {
    return address.addressId
      ? { ...address }
      : ({
        country: countries.find((country) => country.countryId === 1),
        isDefault: myAddressesData.addresses.length === 0,
      } as UserAddress);
  }

  function handleChange(value: any, fieldName: AddressFields) {
    const modifiedAddress = { ...newAddress } as any;

    switch (fieldName) {
      case AddressFields.country:
        value = countries.find((country) => country.countryId === value);

        modifiedAddress.province = undefined;
        modifiedAddress.city = undefined;
        modifiedAddress.region = undefined;
        break;
      case AddressFields.province:
        value = provinces.find((province) => province.provinceId === value);

        modifiedAddress.city = undefined;
        modifiedAddress.region = undefined;
        break;
      case AddressFields.city:
        value = cities.find((city) => city.cityId === value);

        modifiedAddress.region = undefined;
        break;
      case AddressFields.region:
        value = regions.find((region) => region.regionId === value);
        break;
    }

    modifiedAddress[fieldName] = value;
    setNewAddress(modifiedAddress);
  }

  useEffect(() => {
    // TODO Should be deleted after the all regions adding to the admin
    if (newAddress?.city?.cityId && !newAddress?.region?.regionId && newAddress?.province.provinceId !== 1) {
      setNewAddress((prev) => (
        {
          ...prev,
          region: { regionId: newAddress.city.cityId, name: newAddress.city.name, city: { ...newAddress.city } }
        }
      ))
    }
  }, [newAddress]);

  const handleRecipientAddressSave = () => {

    if (isRecipientAddress) {
      const errors = {} as any;

      if (!newAddress.country) {
        errors.country = "required";
      }
      if (!newAddress.province) {
        errors.province = "required";
      }
      if (!newAddress.city) {
        errors.city = "required";
      }
      if (!newAddress.street) {
        errors.street = "required";
      }
      if (!newAddress.building) {
        errors.building = "required";
      }

      setErrors(() => errors);

      if (!Object.keys(errors).length) {
        onSave(true, newAddress);
      }
    }
  }

  async function addAddress() {
    if (isRecipientAddress) {
      const errors = {} as any;

      if (!newAddress.country) {
        errors.country = "required";
      }
      if (!newAddress.province) {
        errors.province = "required";
      }
      if (!newAddress.city) {
        errors.city = "required";
      }

      setErrors(() => errors);

      if (!Object.keys(errors).length) {
        onSave(true, newAddress);
      }
    } else {
      try {
        setErrors({});

        return userAddressesService
          .createAddress(newAddress)
          .then((response: any) => {
            if (response && onSave) {
              onSave(true, newAddress);
            }
          })
          .catch((e) => {
            if (e instanceof AxiosError) {
              setErrors(mapValidationErrors(e));
            }
          });
      } catch (e) {
        if (e instanceof AxiosError) {
          setErrors(mapValidationErrors(e));
        }
      }
    }
  }

  async function editAddress() {
    const request = {} as { [key: string]: any };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Object.keys(newAddress).forEach((key: AddressFields) => {
      if (newAddress[key] !== address[key]) {
        request[key] = newAddress[key];
      }
    });

    if (Object.keys(request).length) {
      request.addressId = newAddress.addressId;

      try {
        userAddressesService
          .editAddress(address.addressId, request)
          .then((response: UserAddressUpdateMeta) => {
            if (response.acknowledged) {
              onSave(true, newAddress);
            }
          })
          .catch((e) => {
            if (e instanceof AxiosError) {
              setErrors(mapValidationErrors(e));
            }
          });
      } catch (e) {
        if (e instanceof AxiosError) {
          setErrors(mapValidationErrors(e));
        }
      }
    } else {
      onSave(false);
    }
  }

  return (
    <>
      <DialogContent>
        <Grid container columnSpacing={"1rem"} rowSpacing={"1rem"}>
          {isRecipientAddress ? (
            ""
          ) : (
            <Grid item xs={matchesPhone ? 12 : 6}>
              <BarekamInput
                customInputVariant={"standard"}
                placeholder={getMessage(
                  "address_name",
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )}
                value={newAddress.name}
                onChange={(event) =>
                  handleChange(event.target.value, AddressFields.name)
                }
                width={"100%"}
                error={!!errors["name"]}
                errorMessage={
                  errors["name"] &&
                  getMessage(
                    errors["name"],
                    MessageSections.USER,
                    messageCache,
                    languageId,
                  )
                }
                required
                validatePattern
              />
            </Grid>
          )}

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamPhoneInput key={'phone-number'}
              value={newAddress.phoneNumber || ""}
              placeholder={getMessage('phone_number', MessageSections.USER, messageCache, languageId)}
              onChange={(value: string) => {
                handleChange(value, AddressFields.phoneNumber);
              }}
              error={!!errors['phoneNumber']}
              errorMessage={errors['phoneNumber'] && getMessage(errors['phoneNumber'], MessageSections.USER, messageCache, languageId)} />
          </Grid>

          {isRecipientAddress ? (
            <Grid item xs={matchesPhone ? 12 : 6}>
              <BarekamSelect
                disabled
                variant={"outlined"}
                label={
                  <Message
                    messageKey={"country"}
                    sectionId={MessageSections.USER}
                  />
                }
                value={newAddress.country?.countryId || ""}
                type={"standard"}
                onChange={(event) =>
                  handleChange(
                    event.target.value as CountryModel,
                    AddressFields.country,
                  )
                }
                MenuProps={{
                  PaperProps: { sx: { "& .MuiList-root": { padding: 0 } } },
                }}
                error={!!errors["country"]}
                errorMessage={
                  errors["country"] &&
                  getMessage(
                    errors["country"],
                    MessageSections.USER,
                    messageCache,
                    languageId,
                  )
                }
                required
              >
                {countries.map((country) => (
                  <MenuItem value={country.countryId} key={country.countryId}>
                    {country.name[languageId]}
                  </MenuItem>
                ))}
              </BarekamSelect>
            </Grid>
          ) : (
            ""
          )}

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <Message
                  messageKey={"province"}
                  sectionId={MessageSections.USER}
                />
              }
              value={newAddress.province?.provinceId || ""}
              type={"standard"}
              onChange={(event) =>
                handleChange(
                  event.target.value as ProvinceModel,
                  AddressFields.province,
                )
              }
              MenuProps={{
                PaperProps: { sx: { "& .MuiList-root": { padding: 0 } } },
              }}
              error={!!errors["province"]}
              errorMessage={
                errors["province"] &&
                getMessage(
                  errors["province"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
            >
              {provinces &&
                provinces
                  .filter(
                    (province) =>
                      province.country.countryId ===
                      newAddress.country?.countryId,
                  )
                  .map((province) => (
                    <MenuItem
                      value={province.provinceId}
                      key={province.provinceId}
                    >
                      <ListItemText
                        style={{ margin: "unset" }}
                        primary={province.name[languageId]}
                      />
                    </MenuItem>
                  ))}
            </BarekamSelect>
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <Message messageKey={"city"} sectionId={MessageSections.USER} />
              }
              value={newAddress.city?.cityId || ""}
              type={"standard"}
              onChange={(event) =>
                handleChange(
                  event.target.value as CityModel,
                  AddressFields.city,
                )
              }
              MenuProps={{
                PaperProps: { sx: { "& .MuiList-root": { padding: 0 } } },
              }}
              error={!!errors["city"]}
              errorMessage={
                errors["city"] &&
                getMessage(
                  errors["city"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
            >
              {cities &&
                cities
                  .filter(
                    (city) =>
                      city.province.provinceId ===
                      newAddress.province?.provinceId,
                  )
                  .map((city) => (
                    <MenuItem value={city.cityId} key={city.cityId}>
                      <ListItemText
                        style={{ margin: "unset" }}
                        primary={city.name[languageId]}
                      />
                    </MenuItem>
                  ))}
            </BarekamSelect>
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <Message
                  messageKey={"region"}
                  sectionId={MessageSections.USER}
                />
              }
              value={newAddress.region?.regionId || ""}
              type={"standard"}
              onChange={(event) =>
                handleChange(
                  event.target.value as ProvinceModel,
                  AddressFields.region,
                )
              }
              MenuProps={{
                PaperProps: { sx: { "& .MuiList-root": { padding: 0 } } },
              }}
              error={!!errors["region"]}
              errorMessage={
                errors["region"] &&
                getMessage(
                  errors["region"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              disabled={newAddress.city?.cityId !== 1}
            >
              {regions &&
                regions
                  .filter(
                    (region) => region.city.cityId === newAddress.city?.cityId,
                  )
                  .map((region) => (
                    <MenuItem value={region.regionId} key={region.regionId}>
                      <ListItemText
                        style={{ margin: "unset" }}
                        primary={region.name[languageId]}
                      />
                    </MenuItem>
                  ))}
            </BarekamSelect>
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "street",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newAddress.street}
              onChange={(event) =>
                handleChange(event.target.value, AddressFields.street)
              }
              width={"100%"}
              error={!!errors["street"]}
              errorMessage={
                errors["street"] &&
                getMessage(
                  errors["street"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              key={"street"}
              required
              validatePattern
            />
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "building",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newAddress.building}
              onChange={(event) =>
                handleChange(event.target.value, AddressFields.building)
              }
              width={"100%"}
              error={!!errors["building"]}
              errorMessage={
                errors["building"] &&
                getMessage(
                  errors["building"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              key={"building"}
              required
            />
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "entrance",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newAddress.entrance}
              onChange={(event) =>
                handleChange(event.target.value, AddressFields.entrance)
              }
              width={"100%"}
              error={!!errors["entrance"]}
              errorMessage={
                errors["entrance"] &&
                getMessage(
                  errors["entrance"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              key={"entrance"}
            />
          </Grid>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "floor",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newAddress.floor}
              onChange={(event) =>
                handleChange(event.target.value, AddressFields.floor)
              }
              width={"100%"}
              error={!!errors["floor"]}
              errorMessage={
                errors["floor"] &&
                getMessage(
                  errors["floor"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              key={"floor"}
            />
          </Grid>
          {/* <Grid item xs={matchesPhone ? 12 : 6}>
                        <BarekamInput customInputVariant={"standard"}
                                      placeholder={getMessage('apartment', MessageSections.USER, messageCache, languageId)}
                                      value={newAddress.apartment}
                                      onChange={(event) => handleChange(event.target.value, AddressFields.apartment)}
                                      width={'100%'}
                                      error={!!errors['apartment']}
                                      errorMessage={errors['apartment'] && getMessage(errors['apartment'], MessageSections.USER, messageCache, languageId)}
                                      key={'apartment'}/>
                    </Grid> */}

          {isRecipientAddress ? (
            ""
          ) : (
            <Grid item xs={matchesPhone ? 12 : 6}>
              <BarekamInput
                customInputVariant={"standard"}
                placeholder={getMessage(
                  "description",
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )}
                value={newAddress.description}
                onChange={(event) =>
                  handleChange(event.target.value, AddressFields.description)
                }
                width={"100%"}
                error={!!errors["description"]}
                errorMessage={
                  errors["description"] &&
                  getMessage(
                    errors["description"],
                    MessageSections.USER,
                    messageCache,
                    languageId,
                  )
                }
                key={"description"}
              />
            </Grid>
          )}
        </Grid>
        {errors["default"] ? (
          <Alert severity="error">{errors["default"]}</Alert>
        ) : (
          <></>
        )}
      </DialogContent>

      <StandardDialogFooter
        saveLabel={
          <Message
            messageKey={address.addressId ? "save" : "add_address"}
            sectionId={MessageSections.USER}
          />
        }
        onSave={() =>
          isRecipientAddress
            ? handleRecipientAddressSave()
            : address.addressId
              ? editAddress()
              : addAddress()
        }
        onCancel={() => onSave(false)}
      />
    </>
  );
};

enum AddressFields {
  name = "name",
  country = "country",
  province = "province",
  city = "city",
  region = "region",
  street = "street",
  building = "building",
  entrance = "entrance",
  floor = "floor",
  apartment = "apartment",
  isDefault = "isDefault",
  zipCode = "zipCode",
  description = "description",
  phoneNumber = "phoneNumber",
}

interface EditRecipientProps extends PaperProps {
  address: UserAddress;
  onSave: (withChange: boolean, address?: UserAddress) => void;
  isRecipientAddress?: boolean;
}
